import { queryOptions, useMutation } from "@tanstack/react-query";
import {
  fetchOnboardingImportOverview,
  fetchOnboardingImportRun,
  startOnboardingImport,
} from "@/features/admin/api/onboarding-import.api";

/** One status read per second: the worker reports after each tenant, and a run is short. */
const RUN_POLL_INTERVAL_MS = 1_000;
/** Another admin's run cannot be polled directly, so the overview is re-read until it ends. */
export const OTHER_RUN_POLL_INTERVAL_MS = 5_000;

export const onboardingImportKeys = {
  all: ["admin", "onboarding-import"] as const,
  overview: () => [...onboardingImportKeys.all, "overview"] as const,
  run: (correlationId: string) => [...onboardingImportKeys.all, "run", correlationId] as const,
} as const;

export function onboardingImportOverviewQueryOptions() {
  return queryOptions({
    queryKey: onboardingImportKeys.overview(),
    queryFn: fetchOnboardingImportOverview,
    refetchInterval: (query) => {
      const activeRun = query.state.data?.activeRun;
      return activeRun && !activeRun.isOwnRun ? OTHER_RUN_POLL_INTERVAL_MS : false;
    },
  });
}

/** Polls a run until it is done or failed; the final report then stays in the cache. */
export function onboardingImportRunQueryOptions(correlationId: string | null) {
  return queryOptions({
    queryKey: onboardingImportKeys.run(correlationId ?? ""),
    queryFn: () => fetchOnboardingImportRun(correlationId!),
    enabled: correlationId !== null,
    refetchInterval: (query) => (query.state.data?.phase === "running" || !query.state.data ? RUN_POLL_INTERVAL_MS : false),
    staleTime: Infinity,
    meta: { skipGlobalError: true },
  });
}

export function useStartOnboardingImport() {
  return useMutation({
    mutationFn: startOnboardingImport,
    meta: { skipGlobalError: true },
  });
}
